import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { 
  ShoppingCart, 
  ArrowLeft, 
  Plus, 
  Minus, 
  Search, 
  User, 
  Phone, 
  Trash2, 
  Save, 
  DollarSign,
  Pause,
  QrCode,
  Clock,
  Loader2,
  Package,
  UserPlus,
  CalendarIcon,
  ScanFace
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { WorkingQRScanModal } from '@/components/WorkingQRScanModal';
import { AddFaceRecordModal } from '@/components/AddFaceRecordModal';
import type { ProductData } from '@/hooks/use-working-qr-scan';
import { supabase } from '@/integrations/supabase/client';
import { customerService, productService } from '@/lib/packages';
import { cn, formatTurkishPhoneForDatabase } from '@/lib/utils';
import { logActivity } from '@/lib/activity-log';
import { useLocation as useLocationContext } from '@/contexts/LocationContext';
type ProductRow = {
  id: string;
  company_id: string;
  name: string;
  item_code: string;
  price: number;
  quantity_on_hand: number;
  current_cost: number | null;
  initial_cost: number | null;
  [key: string]: any;
};

type CustomerRow = {
  id: string;
  company_id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  email: string | null;
  [key: string]: any;
};

type PaymentType =
  | 'cash'
  | 'credit_card'
  | 'odero'
  | 'paro'
  | 'transfer'
  | 'promissory'
  | 'other';



interface SaleLineItem {
  id: string;
  productId: string;
  name: string;
  code: string;
  price: number;
  quantity: number;
  discount: number;
  subtotal: number;
  onHold: boolean;
  note: string;
}

interface Customer {
  id?: string;
  name: string;
  phone: string;
  email?: string;
}

interface Payment {
  id: string;
  amount: number;
  method: PaymentType;
  reference?: string;
  note?: string;
}

export default function NewSale() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { companyId, loading: authLoading, user, userRole } = useAuth();
  const { currentLocation } = useLocationContext();
  
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  const [showProductResults, setShowProductResults] = useState(false);
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showQRScan, setShowQRScan] = useState(false);
  
  const [lineItems, setLineItems] = useState<SaleLineItem[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [saleNotes, setSaleNotes] = useState('');
  const [isSaleOnHold, setIsSaleOnHold] = useState(false);
  const [transactionDate, setTransactionDate] = useState<Date>(new Date());
  
  
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '' });
  const [newPayment, setNewPayment] = useState({
    amount: 0,
    method: 'cash' as PaymentType,
    reference: '',
    note: ''
  });
  
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddFaceModal, setShowAddFaceModal] = useState(false);
  const [customerHasNoFaceRecord, setCustomerHasNoFaceRecord] = useState(false);
  const [userAssignedLocationId, setUserAssignedLocationId] = useState<string | null>(null);

  const getCustomerName = (customer: CustomerRow) => {
    return `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Adsız Müşteri';
  };

  const checkCustomerHasFaceRecord = useCallback(async (customerId: string): Promise<boolean> => {
    try {
      const { data } = await (supabase as any)
        .from('face_templates')
        .select('id')
        .eq('customer_id', customerId)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();
      return !!data;
    } catch {
      return false;
    }
  }, []);

  // Debounced search for customers
  const searchCustomers = useCallback(async (searchTerm: string) => {
    if (!companyId || searchTerm.length < 2) {
      setCustomers([]);
      return;
    }

    setLoadingCustomers(true);
    try {
      const results = await customerService.searchCustomers(companyId, searchTerm, 20);
      setCustomers(results);
    } catch (error) {
      console.error('Error searching customers:', error);
      toast({
        title: "Hata",
        description: "Müşteri arama sırasında hata oluştu",
        variant: "destructive",
      });
    } finally {
      setLoadingCustomers(false);
    }
  }, [companyId, toast]);

  // Debounced search for products
  const searchProducts = useCallback(async (searchTerm: string) => {
    if (!companyId || searchTerm.length < 2) {
      setProducts([]);
      return;
    }

    setLoadingProducts(true);
    try {
      const results = await productService.searchProducts(companyId, searchTerm, 20);
      setProducts(results);
    } catch (error) {
      console.error('Error searching products:', error);
      toast({
        title: "Hata",
        description: "Ürün arama sırasında hata oluştu",
        variant: "destructive",
      });
    } finally {
      setLoadingProducts(false);
    }
  }, [companyId, toast]);

  // Initialize from package data if coming from package
  useEffect(() => {
    const packageData = location.state;
    if (packageData && packageData.fromPackage) {
      // Set customer if exists
      if (packageData.customer) {
        setCustomer({
          id: packageData.customer.id,
          name: packageData.customer.name,
          phone: '', // We'll need to fetch this if required
          email: '', // We'll need to fetch this if required
        });
        setCustomerSearch(packageData.customer.name);
      }

      // Set line items
      if (packageData.items && packageData.items.length > 0) {
        setLineItems(packageData.items);
      }

      // Set notes
      if (packageData.notes) {
        setSaleNotes(packageData.notes);
      }

      // Show toast notification
      toast({
        title: "Paket Yüklendi",
        description: `${packageData.items?.length || 0} ürün ile satış sayfası hazırlandı`,
      });
    }
  }, [location.state, toast]);

  // Effect for customer search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch) {
        searchCustomers(customerSearch);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [customerSearch, searchCustomers]);

  // Effect for product search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearch) {
        searchProducts(productSearch);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [productSearch, searchProducts]);

  // Always keep user's assigned location as fallback for write operations.
  useEffect(() => {
    const fetchAssignedLocation = async () => {
      if (!user?.id) {
        setUserAssignedLocationId(null);
        return;
      }

      const { data, error } = await supabase
        .from('user_profiles')
        .select('location_id')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching assigned location for sale:', error);
        return;
      }

      setUserAssignedLocationId(data?.location_id ?? null);
    };

    void fetchAssignedLocation();
  }, [user?.id]);

  const handleCustomerSelect = async (customerRow: CustomerRow) => {
    const customerData: Customer = {
      id: customerRow.id,
      name: getCustomerName(customerRow),
      phone: customerRow.phone || '',
      email: customerRow.email || undefined
    };
    setCustomer(customerData);
    setCustomerSearch(customerData.name);
    setShowCustomerResults(false);
    setCustomers([]);

    if (customerRow.id && companyId) {
      const hasFace = await checkCustomerHasFaceRecord(customerRow.id);
      setCustomerHasNoFaceRecord(!hasFace);
    } else {
      setCustomerHasNoFaceRecord(false);
    }
  };

  const handleProductSelect = (product: ProductRow) => {
    addLineItem(product);
    setProductSearch('');
    setShowProductResults(false);
    setProducts([]);
  };

  const handleTemporaryCustomer = () => {
    setCustomer(null);
    setCustomerSearch('');
    setShowCustomerResults(false);
    setCustomers([]);
    setCustomerHasNoFaceRecord(false);
  };

  const addLineItem = (product: ProductRow) => {
    setLineItems(prev => {
      // Use functional update to avoid race conditions
      const existingItem = prev.find(item => item.productId === product.id);
      
      if (existingItem) {
        // Update existing item quantity
        return prev.map(item => {
          if (item.id === existingItem.id) {
            const updated = { ...item, quantity: item.quantity + 1 };
            // Recalculate subtotal
            updated.subtotal = (updated.price * updated.quantity) - updated.discount;
            return updated;
          }
          return item;
        });
      } else {
        // Add new item
        const newItem: SaleLineItem = {
          id: Date.now().toString(),
          productId: product.id,
          name: product.name,
          code: product.item_code || '',
          price: product.price || 0,
          quantity: 1,
          discount: 0,
          subtotal: product.price || 0,
          onHold: false,
          note: ''
        };
        return [...prev, newItem];
      }
    });
    setShowProductDialog(false);
  };

  const updateLineItem = (itemId: string, field: keyof SaleLineItem, value: any) => {
    setLineItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const updated = { ...item, [field]: value };
        // Recalculate subtotal
        updated.subtotal = (updated.price * updated.quantity) - updated.discount;
        return updated;
      }
      return item;
    }));
  };

  const removeLineItem = (itemId: string) => {
    setLineItems(prev => prev.filter(item => item.id !== itemId));
  };

  const toggleItemHold = (itemId: string) => {
    updateLineItem(itemId, 'onHold', !lineItems.find(item => item.id === itemId)?.onHold);
  };

  const toggleAllItemsHold = (allOnHold: boolean) => {
    setLineItems(prev => prev.map(item => ({ ...item, onHold: allOnHold })));
  };

  const addPayment = () => {
    if (newPayment.amount <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Payment amount must be greater than zero",
        variant: "destructive",
      });
      return;
    }

    const payment: Payment = {
      id: Date.now().toString(),
      amount: newPayment.amount,
      method: newPayment.method,
      reference: newPayment.reference || undefined,
      note: newPayment.note || undefined
    };

    setPayments(prev => [...prev, payment]);
    setNewPayment({ amount: 0, method: 'cash', reference: '', note: '' });
    setShowPaymentDialog(false);
    
    toast({
      title: "Payment Added",
      description: `$${payment.amount} payment recorded`,
    });
  };

  const removePayment = (paymentId: string) => {
    setPayments(prev => prev.filter(p => p.id !== paymentId));
  };

  const createCustomer = async () => {
    if (!newCustomer.name || !newCustomer.phone || !companyId) {
      toast({
        title: "Eksik Bilgi",
        description: "İsim ve telefon gerekli",
        variant: "destructive",
      });
      return;
    }

    try {
      const [firstName, ...lastNameParts] = newCustomer.name.split(' ');
      const lastName = lastNameParts.join(' ') || null;
      
      const { data, error } = await supabase
        .from('customers')
        .insert({
          company_id: companyId,
          first_name: firstName,
          last_name: lastName,
          phone: formatTurkishPhoneForDatabase(newCustomer.phone),
          email: newCustomer.email || null,
          color: 'mavi',
          tags_manual: [],
          visit_count_total: 0,
          notes_count: 0,
          streak_days: 0
        })
        .select()
        .single();

      if (error) throw error;

      // Log activity for customer creation
      if (companyId) {
        await logActivity({
          action: 'create',
          entity: 'customer',
          entityId: data.id,
          detail: {
            name: newCustomer.name,
            phone: newCustomer.phone,
            email: newCustomer.email || null,
          },
          locationId: currentLocation?.id || null,
          companyId,
          userId: user?.id || null,
        });
      }

      const customer: Customer = {
        id: data.id,
        name: newCustomer.name,
        phone: newCustomer.phone,
        email: newCustomer.email || undefined
      };

      setCustomer(customer);
      setCustomerSearch(customer.name);
      setNewCustomer({ name: '', phone: '', email: '' });
      setShowCustomerDialog(false);

      toast({
        title: "Müşteri Oluşturuldu",
        description: `${customer.name} başarıyla eklendi`,
      });

      if (data.id) {
        const hasFace = await checkCustomerHasFaceRecord(data.id);
        setCustomerHasNoFaceRecord(!hasFace);
      } else {
        setCustomerHasNoFaceRecord(false);
      }
    } catch (error) {
      console.error('Error creating customer:', error);
      toast({
        title: "Hata",
        description: "Müşteri oluşturulurken hata oluştu",
        variant: "destructive",
      });
    }
  };

  const calculateTotals = () => {
    const activeItems = lineItems.filter(item => !item.onHold);
    const onHoldItems = lineItems.filter(item => item.onHold);
    
    const subtotal = lineItems.reduce((sum, item) => sum + item.subtotal, 0);
    const activeSubtotal = activeItems.reduce((sum, item) => sum + item.subtotal, 0);
    const onHoldSubtotal = onHoldItems.reduce((sum, item) => sum + item.subtotal, 0);
    
    const totalPayments = payments.reduce((sum, payment) => sum + payment.amount, 0);
    const activeItemCount = activeItems.length;
    const onHoldItemCount = onHoldItems.length;
    
    // Always calculate balance based on all items that require payment
    const paymentRequired = onHoldItemCount > 0 && activeItemCount > 0 ? 
      subtotal : // Mixed sale - pay for all items
      subtotal; // Either all hold or all regular - pay for all items
    const balance = paymentRequired - totalPayments;
    
    return { 
      subtotal, 
      activeSubtotal,
      onHoldSubtotal,
      totalPayments, 
      balance, 
      activeItemCount,
      onHoldItemCount,
      totalItemCount: lineItems.length
    };
  };

  const { subtotal, activeSubtotal, onHoldSubtotal, totalPayments, balance, activeItemCount, onHoldItemCount, totalItemCount } = calculateTotals();

  const handleSave = async () => {
    // Prevent concurrent save operations
    if (isSaving) {
      return;
    }

    if (!customer || !companyId) {
      toast({
        title: "Müşteri Gerekli",
        description: "Lütfen bir müşteri seçin veya oluşturun",
        variant: "destructive",
      });
      return;
    }

    if (lineItems.length === 0) {
      toast({
        title: "Ürün Yok",
        description: "Lütfen satışa en az bir ürün ekleyin",
        variant: "destructive",
      });
      return;
    }

    const effectiveLocationId = userRole === 'admin'
      ? (currentLocation?.id ?? userAssignedLocationId ?? null)
      : (userAssignedLocationId ?? currentLocation?.id ?? null);
    if (!effectiveLocationId) {
      toast({
        title: "Lokasyon Gerekli",
        description: userRole === 'admin'
          ? "Satış kaydı için aktif bir lokasyon seçili olmalıdır"
          : "Kullanıcı profilinde lokasyon ataması bulunamadı",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      // Separate on-hold items from regular sale items
      const onHoldItems = lineItems.filter(item => item.onHold);
      const regularItems = lineItems.filter(item => !item.onHold);

      // Save on-hold items
      if (onHoldItems.length > 0) {
        for (const item of onHoldItems) {
          const { error } = await supabase
            .from('on_holds')
            .insert({
              company_id: companyId,
              location_id: effectiveLocationId,
              customer_id: customer.id!,
              product_id: item.productId,
              hold_quantity: item.quantity,
              hold_price: item.price,
              hold_cost: item.price * 0.7, // Assume 70% cost ratio, adjust as needed
              status: 'on_hold',
            });

          if (error) throw error;
        }
      }

      // Create regular sale if there are items not on hold or if it's an all on-hold sale
      if (regularItems.length > 0 || isSaleOnHold) {
        // Create regular sale (only for non-on-hold items if mixed sale, or all items if full on-hold sale)
        const itemsForSale = isSaleOnHold ? lineItems : regularItems;
        const saleTotal = itemsForSale.reduce((sum, item) => sum + item.subtotal, 0);
        const totalPaid = payments.reduce((sum, payment) => sum + payment.amount, 0);
        
        const { data: saleData, error: saleError } = await supabase
          .from('sales')
          .insert({
            company_id: companyId,
            location_id: effectiveLocationId,
            customer_id: customer.id!,
            status: 'open',
            total_gross: saleTotal,
            total_discount: itemsForSale.reduce((sum, item) => sum + item.discount, 0),
            total_net: saleTotal,
            total_paid: totalPaid,
            balance_due: saleTotal - totalPaid,
            note: saleNotes || null,
            sale_datetime: transactionDate.toISOString(),
            created_by: user?.id || null,
          })
          .select()
          .single();

        if (saleError) throw saleError;

        // Log activity for sale creation
        if (companyId) {
          await logActivity({
            action: 'create',
            entity: 'sale',
            entityId: saleData.id,
            detail: {
              customer_id: customer.id,
              total_net: saleTotal,
              total_paid: totalPaid,
              item_count: itemsForSale.length,
            },
            locationId: effectiveLocationId,
            companyId,
            userId: user?.id || null,
          });
        }

        // Add sale items (only the ones for this sale)
        for (const item of itemsForSale) {
          const { error: itemError } = await supabase
            .from('sale_items')
            .insert({
              company_id: companyId,
              location_id: effectiveLocationId,
              sale_id: saleData.id,
              product_id: item.productId,
              quantity: item.quantity,
              sale_price_each: item.price,
              discount: item.discount,
              line_total_gross: item.quantity * item.price,
              line_total_net: item.subtotal,
              was_on_hold: item.onHold,
              was_bayi_emanet: false,
              bayi_emanet_returned: false,
            });

          if (itemError) throw itemError;

          // Update inventory (only for regular items, not on-hold)
          if (!item.onHold) {
            // Fetch current product to get current quantity
            const { data: currentProduct, error: productFetchError } = await supabase
              .from('products')
              .select('quantity_on_hand')
              .eq('id', item.productId)
              .eq('company_id', companyId)
              .single();

            if (productFetchError) {
              console.error('Error fetching product for inventory update:', productFetchError);
              throw new Error('Failed to fetch product inventory');
            }

            // Calculate new quantity (allow negative stock)
            const currentQuantity = currentProduct?.quantity_on_hand || 0;
            const newQuantity = currentQuantity - item.quantity;

            // Update product inventory
            const { error: inventoryUpdateError } = await supabase
              .from('products')
              .update({
                quantity_on_hand: newQuantity,
              })
              .eq('id', item.productId)
              .eq('company_id', companyId);

            if (inventoryUpdateError) {
              console.error('Error updating product inventory:', inventoryUpdateError);
              throw new Error('Failed to update product inventory');
            }

            // Create inventory movement record
            const { error: inventoryError } = await supabase
              .from('inventory_movements')
              .insert({
                company_id: companyId,
                location_id: effectiveLocationId,
                product_id: item.productId,
                movement_type: 'sale_out',
                qty: item.quantity,
                related_table: 'sales',
                related_id: saleData.id,
                actor_user_id: user?.id || null,
              });

            if (inventoryError) throw inventoryError;
          }
        }

        // Add payments
        for (const payment of payments) {
          // For senet (promissory) payments, create an open payment record
          const isOpenPayment = payment.method === 'promissory';
          
          const { error: paymentError } = await supabase
            .from('sale_payments')
            .insert({
              company_id: companyId,
              location_id: effectiveLocationId,
              sale_id: saleData.id,
              amount: payment.amount,
              payment_type: payment.method,
              ref: payment.reference || null,
              paid_at: new Date().toISOString(),
              original_amount: isOpenPayment ? payment.amount : null,
              remaining_amount: isOpenPayment ? payment.amount : 0,
              payment_status: isOpenPayment ? 'open' : 'completed',
              parent_payment_id: null,
            });

          if (paymentError) throw paymentError;
        }

        // Create calendar reminder for customer satisfaction call (4 days after sale date)
        // Only for regular sales (not for on-hold only sales)
        if (regularItems.length > 0) {
          // Use the sale date (transactionDate) instead of current date
          const saleDate = new Date(transactionDate);
          const reminderDate = new Date(saleDate);
          reminderDate.setDate(reminderDate.getDate() + 4);
          
          const customerName = customer.name || 'Müşteri';
          
          const { error: calendarError } = await supabase
            .from('calendar_events')
            .insert({
              company_id: companyId,
              location_id: effectiveLocationId,
              title: 'Memnuniyet Araması',
              description: `${customerName} müşteriyi arayıp sürecin nasıl geçtiğini sorun, memnuniyet ölçün`,
              start_date: reminderDate.toISOString().split('T')[0],
              start_time: '10:00:00',
              event_type: 'follow_up',
              category: 'sales',
              priority: 'medium',
              status: 'scheduled',
              customer_id: customer.id!,
              sale_id: saleData.id,
              reminder_minutes_before: [60, 1440], // 1 hour and 1 day before
              metadata: {
                purpose: 'customer_satisfaction',
                sale_amount: saleTotal,
                product_count: itemsForSale.length
              }
            });

          if (calendarError) {
            console.warn('Calendar reminder creation failed:', calendarError);
            // Don't throw error here - sale should still succeed even if reminder fails
          }
        }

      }

      // Show appropriate success message
      if (onHoldItems.length > 0 && regularItems.length > 0) {
        toast({
          title: "Karma Satış Kaydedildi",
          description: `${regularItems.length} ürün satıldı, ${onHoldItems.length} ürün beklemeye alındı`,
        });
      } else if (onHoldItems.length > 0) {
        toast({
          title: "Emanet Satış Kaydedildi",
          description: "Ödeme alındı, ürünler ileriki tarihte teslim edilecek",
        });
      } else {
        toast({
          title: "Satış Kaydedildi", 
          description: "Satış başarıyla kaydedildi",
        });
      }

      navigate('/sales');
    } catch (error) {
      console.error('Error saving sale:', error);
      toast({
        title: "Hata",
        description: "Satış kaydedilirken hata oluştu",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/sales')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Satışlara Dön
          </Button>
          
          <div className="flex-1">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold">Yeni Satış</h1>
                <p className="text-muted-foreground">Çok satırlı ödeme ile yeni satış oluşturun</p>
              </div>
              <div className="flex items-center space-x-4">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">İşlem Tarihi</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-[200px] justify-start text-left font-normal",
                          !transactionDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {transactionDate ? format(transactionDate, "dd MMM yyyy", { locale: tr }) : "Tarih seçin"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={transactionDate}
                        onSelect={(date) => date && setTransactionDate(date)}
                        initialFocus
                        locale={tr}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="sale-on-hold"
                    checked={isSaleOnHold}
                    onCheckedChange={(checked) => {
                      const newValue = checked as boolean;
                      setIsSaleOnHold(newValue);
                      // Auto-select all items when toggling global hold
                      if (newValue) {
                        toggleAllItemsHold(true);
                      }
                    }}
                  />
                  <Label htmlFor="sale-on-hold" className="text-sm font-medium">
                    Bu satış beklemede
                  </Label>
                </div>
                
                {lineItems.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const allHeld = lineItems.every(item => item.onHold);
                        toggleAllItemsHold(!allHeld);
                      }}
                      className="gap-2"
                    >
                      <Pause className="w-4 h-4" />
                      {lineItems.every(item => item.onHold) ? 'Tümünü Hemen Sat' : 'Tümünü Beklet'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Satışı Kaydet
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Sale Status Info */}
        {onHoldItemCount > 0 && (
          <div className="bg-orange-50 dark:bg-orange-950/50 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
              <Clock className="w-5 h-5" />
              <span className="font-medium">
                {onHoldItemCount > 0 && activeItemCount > 0 ? 'Karma satış' : 'Emanet satış'}
              </span>
            </div>
            <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
              {onHoldItemCount > 0 && activeItemCount > 0 
                ? `${activeItemCount} ürün hemen teslim, ${onHoldItemCount} ürün emanet (stoktan düşürülmez)`
                : 'Emanet satış: Ödeme alınır, stoktan düşürülmez, ileriki tarihte teslim edilir'
              }
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Müşteri Bilgileri
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Müşteri Seç</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Müşteri ara..."
                        value={customerSearch}
                        onChange={(e) => {
                          setCustomerSearch(e.target.value);
                          setShowCustomerResults(e.target.value.length > 0);
                          if (e.target.value === '') {
                            setCustomer(null);
                            setCustomers([]);
                          }
                        }}
                        onFocus={() => setShowCustomerResults(customerSearch.length > 0)}
                        onBlur={() => setTimeout(() => setShowCustomerResults(false), 150)}
                        className="pl-10"
                      />
                      
                      {showCustomerResults && (
                        <div className="absolute top-full mt-1 w-full bg-popover border border-border rounded-md shadow-lg z-10 max-h-48 overflow-y-auto backdrop-blur-sm">
                          {loadingCustomers ? (
                            <div className="p-3 text-muted-foreground text-center">
                              <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                              Müşteriler aranıyor...
                            </div>
                          ) : customers.length > 0 ? (
                            customers.map((customer) => (
                              <div
                                key={customer.id}
                                className="flex items-center gap-3 p-3 hover:bg-accent cursor-pointer"
                                onClick={() => handleCustomerSelect(customer)}
                              >
                                <Avatar className="w-8 h-8">
                                  <AvatarFallback className="bg-accent/20 text-accent text-xs">
                                    {getCustomerName(customer).split(' ').map(n => n[0]).join('')}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium">{getCustomerName(customer)}</p>
                                  <p className="text-sm text-muted-foreground">{customer.email || customer.phone}</p>
                                </div>
                              </div>
                            ))
                          ) : customerSearch.length >= 2 ? (
                            <div className="p-3 text-muted-foreground text-center">
                              Müşteri bulunamadı
                            </div>
                          ) : (
                            <div className="p-3 text-muted-foreground text-center">
                              En az 2 karakter girin
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <Button 
                      variant="outline" 
                      onClick={handleTemporaryCustomer}
                      className="gap-2"
                    >
                      <UserPlus className="w-4 h-4" />
                      Geçici Kayıt
                    </Button>
                    
                    <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
                      <DialogTrigger asChild>
                        <Button variant="outline">
                          <Plus className="w-4 h-4 mr-2" />
                          Yeni
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Yeni Müşteri Oluştur</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="newCustomerName">İsim *</Label>
                            <Input
                              id="newCustomerName"
                              value={newCustomer.name}
                              onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                              placeholder="Müşteri adı..."
                            />
                          </div>
                          <div>
                            <Label htmlFor="newCustomerPhone">Telefon *</Label>
                            <Input
                              id="newCustomerPhone"
                              value={newCustomer.phone}
                              onChange={(e) => setNewCustomer(prev => ({ ...prev, phone: e.target.value }))}
                              placeholder="Telefon numarası..."
                            />
                          </div>
                          <div>
                            <Label htmlFor="newCustomerEmail">E-posta</Label>
                            <Input
                              id="newCustomerEmail"
                              type="email"
                              value={newCustomer.email}
                              onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
                              placeholder="E-posta adresi..."
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" className="flex-1" onClick={() => setShowCustomerDialog(false)}>
                              İptal
                            </Button>
                            <Button className="flex-1" onClick={createCustomer}>
                              Müşteri Oluştur
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground mt-1">
                  Daha sonra doldurulacak kayıtlar için "Geçici Kayıt" seçeneğini kullanın
                </p>
                
                {customer && (
                  <Card className="bg-accent/5">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className="bg-accent/20 text-accent">
                            {customer.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <h4 className="font-medium">{customer.name}</h4>
                          <p className="text-sm text-muted-foreground">{customer.phone}</p>
                          {customer.email && (
                            <p className="text-sm text-muted-foreground">{customer.email}</p>
                          )}
                        </div>
                        {customerHasNoFaceRecord && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowAddFaceModal(true)}
                            className="gap-2"
                          >
                            <ScanFace className="w-4 h-4" />
                            Yüz Kaydı Ekle
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => {
                          setCustomer(null);
                          setCustomerSearch('');
                          setCustomerHasNoFaceRecord(false);
                        }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Ürünler</CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowQRScan(true)}>
                      <QrCode className="w-4 h-4 mr-2" />
                      QR Scan
                    </Button>
                    
                    <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
                      <DialogTrigger asChild>
                        <Button size="sm">
                          <Plus className="w-4 h-4 mr-2" />
                          Ürün Ekle
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Ürün Seç</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="Ürün ara..."
                              value={productSearch}
                              onChange={(e) => {
                                setProductSearch(e.target.value);
                                setShowProductResults(e.target.value.length > 0);
                                if (e.target.value === '') {
                                  setProducts([]);
                                }
                              }}
                              onFocus={() => setShowProductResults(productSearch.length > 0)}
                              className="pl-10"
                            />
                          </div>
                          
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {loadingProducts ? (
                              <div className="p-3 text-muted-foreground text-center">
                                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                                Ürünler aranıyor...
                              </div>
                            ) : products.length > 0 ? (
                              products.map((product) => (
                                <div
                                  key={product.id}
                                  className="flex items-center gap-3 p-3 hover:bg-accent cursor-pointer border rounded"
                                  onClick={() => handleProductSelect(product)}
                                >
                                  <Package className="w-8 h-8 text-muted-foreground" />
                                  <div>
                                    <p className="font-medium">{product.name}</p>
                                    <p className="text-sm text-muted-foreground">
                                      {product.item_code} • ₺{(product.price || 0).toLocaleString('tr-TR')} • Stok: {product.quantity_on_hand}
                                    </p>
                                  </div>
                                </div>
                              ))
                            ) : productSearch.length >= 2 ? (
                              <div className="p-3 text-muted-foreground text-center">
                                Ürün bulunamadı
                              </div>
                            ) : productSearch.length > 0 ? (
                              <div className="p-3 text-muted-foreground text-center">
                                En az 2 karakter girin
                              </div>
                            ) : (
                              <div className="p-3 text-muted-foreground text-center">
                                Ürün aramak için yazmaya başlayın
                              </div>
                            )}
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {lineItems.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <h3 className="font-medium mb-2">Ürün eklenmedi</h3>
                    <p className="text-sm">QR tarayın veya satışa eklemek için ürün seçin</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {lineItems.map((item) => (
                      <div key={item.id} className={cn(
                        "p-4 border rounded-lg",
                        item.onHold && "bg-muted/50 opacity-75"
                      )}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-medium">{item.name}</h4>
                              {item.onHold && (
                                <Badge variant="secondary">
                                  <Pause className="w-3 h-3 mr-1" />
                                  Beklemede
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{item.code}</p>
                            
                            <div className="grid grid-cols-4 gap-2 mt-3">
                              <div>
                                <Label className="text-xs">Miktar</Label>
                                <Input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => updateLineItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                                  min="1"
                                  className="h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Fiyat</Label>
                                <Input
                                  type="number"
                                  value={item.price || ''}
                                  onChange={(e) => {
                                    const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                    updateLineItem(item.id, 'price', value || 0);
                                  }}
                                  placeholder="0.00"
                                  step="0.01"
                                  min="0"
                                  className="h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">İndirim</Label>
                                <Input
                                  type="number"
                                  value={item.discount}
                                  onChange={(e) => updateLineItem(item.id, 'discount', parseFloat(e.target.value) || 0)}
                                  className="h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Ara Toplam</Label>
                                <div className="h-8 px-3 py-2 bg-muted rounded-md text-sm">
                                  ₺{item.subtotal.toLocaleString('tr-TR')}
                                </div>
                              </div>
                            </div>
                            
                            <div className="mt-3 space-y-2">
                              <Input
                                placeholder="Bu ürün için not ekleyin..."
                                value={item.note}
                                onChange={(e) => updateLineItem(item.id, 'note', e.target.value)}
                                className="h-8"
                              />
                              
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`onhold-${item.id}`}
                                    checked={item.onHold}
                                    onCheckedChange={(checked) => updateLineItem(item.id, 'onHold', checked)}
                                  />
                                  <Label htmlFor={`onhold-${item.id}`} className="text-xs">
                                    Beklemede (stoktan düşürme)
                                  </Label>
                                </div>
                                
                                <Button
                                  variant={item.onHold ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => toggleItemHold(item.id)}
                                  className="gap-2"
                                >
                                  <Pause className="w-4 h-4" />
                                  {item.onHold ? 'Beklemededen Çıkar' : 'Beklemede Satış'}
                                </Button>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex flex-col gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => removeLineItem(item.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Summary Sidebar */}
          <div className="space-y-6">
            {/* Order Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Sipariş Özeti</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {onHoldItemCount > 0 && activeItemCount > 0 ? (
                  // Mixed sale
                  <>
                    <div className="flex justify-between">
                      <span>Hemen Satış ({activeItemCount}):</span>
                      <span>₺{activeSubtotal.toLocaleString('tr-TR')}</span>
                    </div>
                    <div className="flex justify-between text-orange-600 dark:text-orange-400">
                      <span>Beklemede ({onHoldItemCount}):</span>
                      <span>₺{onHoldSubtotal.toLocaleString('tr-TR')}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-lg font-semibold">
                      <span>Ödeme Gerekli:</span>
                      <span>₺{subtotal.toLocaleString('tr-TR')}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      * Beklemedeki ürünler: Ödeme alınır, stok düşürülmez
                    </div>
                  </>
                ) : onHoldItemCount > 0 ? (
                  // All on hold (only show when there are actually on-hold items)
                  <>
                    <div className="flex justify-between text-orange-600 dark:text-orange-400">
                      <span>Beklemede Ürünler ({onHoldItemCount}):</span>
                      <span>₺{subtotal.toLocaleString('tr-TR')}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-lg font-semibold">
                      <span>Ödeme Gerekli:</span>
                      <span>₺{subtotal.toLocaleString('tr-TR')}</span>
                    </div>
                    <div className="text-sm text-orange-600 dark:text-orange-400">
                      * Emanet satış: Ödeme alınır, stok düşürülmez
                    </div>
                  </>
                ) : (
                  // Regular sale (no on-hold items)
                  <>
                    <div className="flex justify-between">
                      <span>Ürünler ({totalItemCount}):</span>
                      <span>₺{subtotal.toLocaleString('tr-TR')}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-lg font-semibold">
                      <span>Ara Toplam:</span>
                      <span>₺{subtotal.toLocaleString('tr-TR')}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Payments */}
            <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Ödemeler</CardTitle>
                    <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
                      <DialogTrigger asChild>
                        <Button size="sm">
                          <Plus className="w-4 h-4 mr-2" />
                          Ödeme Ekle
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Ödeme Ekle</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="paymentAmount">Tutar *</Label>
                            <Input
                              id="paymentAmount"
                              type="number"
                              value={newPayment.amount || ''}
                              onChange={(e) => setNewPayment(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                              placeholder="0.00"
                              step="0.01"
                            />
                          </div>
                          <div>
                            <Label>Ödeme Yöntemi</Label>
                            <Select value={newPayment.method} onValueChange={(value: PaymentType) => 
                              setNewPayment(prev => ({ ...prev, method: value }))
                            }>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="cash">Nakit</SelectItem>
                                <SelectItem value="credit_card">Kredi Kartı</SelectItem>
                                <SelectItem value="odero">Odero</SelectItem>
                                <SelectItem value="paro">Paro</SelectItem>
                                <SelectItem value="transfer">Transfer</SelectItem>
                                <SelectItem value="promissory">Senet</SelectItem>
                                <SelectItem value="other">Diğer</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="paymentReference">Referans</Label>
                            <Input
                              id="paymentReference"
                              value={newPayment.reference}
                              onChange={(e) => setNewPayment(prev => ({ ...prev, reference: e.target.value }))}
                              placeholder="İşlem ID, çek numarası vb."
                            />
                          </div>
                          <div>
                            <Label htmlFor="paymentNote">Not</Label>
                            <Textarea
                              id="paymentNote"
                              value={newPayment.note}
                              onChange={(e) => setNewPayment(prev => ({ ...prev, note: e.target.value }))}
                              placeholder="Ödeme notları..."
                              rows={2}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" className="flex-1" onClick={() => setShowPaymentDialog(false)}>
                              İptal
                            </Button>
                            <Button className="flex-1" onClick={addPayment}>
                              Ödeme Ekle
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {payments.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Henüz ödeme eklenmedi
                    </p>
                  ) : (
                    payments.map((payment) => (
                      <div key={payment.id} className="flex items-center justify-between p-2 border rounded">
                        <div>
                          <div className="font-medium">₺{payment.amount.toLocaleString('tr-TR')}</div>
                          <div className="text-xs text-muted-foreground capitalize">
                            {payment.method === 'cash' ? 'Nakit' : 
                             payment.method === 'credit_card' ? 'Kredi Kartı' :
                             payment.method === 'odero' ? 'Odero' :
                             payment.method === 'paro' ? 'Paro' :
                             payment.method === 'transfer' ? 'Transfer' :
                             payment.method === 'promissory' ? 'Senet' : 'Diğer'}
                            {payment.reference && ` • ${payment.reference}`}
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removePayment(payment.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )}
                  
                  {payments.length > 0 && (
                    <>
                      <Separator />
                      {payments.some(p => p.method === 'promissory') ? (
                        <>
                          <div className="flex justify-between font-medium">
                            <span>Ödenecek Açık Hesap:</span>
                            <span>₺{totalPayments.toLocaleString('tr-TR')}</span>
                          </div>
                          <div className="flex justify-between font-bold text-lg">
                            <span>Bakiye:</span>
                            <span className={balance > 0 ? 'text-destructive' : balance < 0 ? 'text-success' : ''}>
                              ₺{Math.abs(balance).toLocaleString('tr-TR')}
                              {balance > 0 && ' borç'}
                              {balance < 0 && ' para üstü'}
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between font-medium">
                            <span>Toplam Ödenen:</span>
                            <span>₺{totalPayments.toLocaleString('tr-TR')}</span>
                          </div>
                          <div className="flex justify-between font-bold text-lg">
                            <span>Bakiye:</span>
                            <span className={balance > 0 ? 'text-destructive' : balance < 0 ? 'text-success' : ''}>
                              ₺{Math.abs(balance).toLocaleString('tr-TR')}
                              {balance > 0 && ' borç'}
                              {balance < 0 && ' para üstü'}
                            </span>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle>Satış Notları</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={saleNotes}
                  onChange={(e) => setSaleNotes(e.target.value)}
                  placeholder="Bu satış hakkında notlar ekleyin..."
                  rows={4}
                />
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Add Face Record Modal - shown when customer has no face record */}
        {customer?.id && companyId && (
          <AddFaceRecordModal
            isOpen={showAddFaceModal}
            onClose={() => setShowAddFaceModal(false)}
            customerId={customer.id}
            customerName={customer.name}
            companyId={companyId}
            onSaved={() => setCustomerHasNoFaceRecord(false)}
          />
        )}

        {/* QR Scan Modal */}
        <WorkingQRScanModal
          open={showQRScan}
          onOpenChange={setShowQRScan}
          onProductFound={useCallback((product: ProductData) => {
            // Convert ProductData to ProductRow format for addLineItem
            const productRow: ProductRow = {
              id: product.id,
              name: product.name,
              item_code: product.item_code,
              price: product.price,
              quantity_on_hand: product.quantity_on_hand,
              category: product.category,
              // Add other required ProductRow fields with defaults
              company_id: companyId || '',
              current_cost: 0,
              initial_cost: 0,
              entry_date: '',
              meta: {},
              created_at: '',
              updated_at: ''
            };
            
            addLineItem(productRow);
          }, [companyId])}
          title="Satışa Ürün Ekle"
        />
      </div>
    </Layout>
  );
}